import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import './styles/Consistency.css';
import Header from './components/Header';
import Footer from './components/Footer';
import OfflineBanner from './components/OfflineBanner';
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginModalProvider } from './context/LoginModalContext';
import { useLoginModal } from './context/LoginModalContext';
import LoginModal from './components/LoginModal';
import { trackPageView } from './utils/analytics';
import { setUserContext } from './utils/sentry';

// Critical path — loaded eagerly (landing page)
import SearchFlights from './pages/SearchFlights';
import ErrorPage from './pages/ErrorPage';

// Secondary pages — loaded only when navigated to
const Home             = lazy(() => import('./pages/Home'));
const CreateAccount    = lazy(() => import('./pages/CreateAccount'));
const MyBookings       = lazy(() => import('./pages/MyBookings'));
const FlightDetails    = lazy(() => import('./pages/FlightDetails'));
const BookingPreview   = lazy(() => import('./pages/BookingPreview'));
const DuffelPayment    = lazy(() => import('./pages/DuffelPayment'));
const PaymentSuccess   = lazy(() => import('./pages/PaymentSuccess'));
const ForgotPassword   = lazy(() => import('./pages/ForgotPassword'));
const VerifyEmail      = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword    = lazy(() => import('./pages/ResetPassword'));
const RescheduleOptions = lazy(() => import('./pages/RescheduleOptions'));
const PrivacyPolicy    = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse       = lazy(() => import('./pages/TermsOfUse'));
const CookiePolicy     = lazy(() => import('./pages/CookiePolicy'));
const About            = lazy(() => import('./pages/About'));
const Profile          = lazy(() => import('./pages/Profile'));
const Loyalty          = lazy(() => import('./pages/Loyalty'));
const Transactions     = lazy(() => import('./pages/Transactions'));
const FAQ              = lazy(() => import('./pages/FAQ'));
const Contact          = lazy(() => import('./pages/Contact'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  const { openLoginModal } = useLoginModal();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      openLoginModal({ returnTo: location.pathname + location.search });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Analytics and error tracking wrapper
function AnalyticsWrapper({ children }) {
  const location = useLocation();
  const { user } = useAuth();

  // Track page views
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Set user context for error tracking
  useEffect(() => {
    if (user) {
      setUserContext(user);
    }
  }, [user]);

  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LoginModalProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SEOHead />
        <AnalyticsWrapper>
        <ScrollToTop />
        <div className="App">
          <OfflineBanner />
          <Header />
          <LoginModal />
          <main id="main-content" tabIndex="-1">
            <Suspense fallback={<div className="page-loading" aria-label="Loading page" />}>
              <Routes>
                <Route path="/" element={<SearchFlights />} />
                <Route path="/search-flights" element={<Navigate to="/" replace />} />
                <Route path="/air-radar" element={<Home />} />
                <Route path="/air-weekly" element={<Navigate to="/air-radar" replace />} />
                <Route path="/flight-details" element={<FlightDetails />} />
                <Route path="/booking-preview" element={<BookingPreview />} />
                <Route path="/duffel-payment" element={<DuffelPayment />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/my-bookings" element={<PrivateRoute><MyBookings /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                <Route path="/loyalty" element={<PrivateRoute><Loyalty /></PrivateRoute>} />
                <Route path="/transactions" element={<PrivateRoute><Transactions /></PrivateRoute>} />
                <Route path="/reschedule/:bookingId" element={<PrivateRoute><RescheduleOptions /></PrivateRoute>} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/create-account" element={<CreateAccount />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<ErrorPage variant="not-found" />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
        </AnalyticsWrapper>
      </Router>
      </LoginModalProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
